import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import RoleForm from "../../components/RoleForm/RoleForm";
import useRole from "../../hooks/useRole";
import useUpdateRole from "../../hooks/useUpdateRole";
import useDeleteRole from "../../hooks/useDeleteRole";
import useAvailablePermissions from "../../hooks/useAvailablePermissions";
import { isSeededRole } from "../../constants/permissionCategories";

const EditRole = () => {
	const navigate = useNavigate();
	const { roleId } = useParams<{ roleId: string }>();

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] = useState<RoleFormValues>({
		name: "",
		permissions: []
	});

	const { data: role } = useRole(roleId ?? "");
	const { data: availablePermissions } = useAvailablePermissions();
	const { mutate: updateRole, isLoading: isUpdating } = useUpdateRole();
	const { mutate: deleteRole, isLoading: isDeleting } = useDeleteRole();

	useEffect(() => {
		if (role) {
			setDefaultValues({
				name: role.name,
				permissions: role.permissions
			});
		}
	}, [role]);

	if (!roleId) {
		return <Navigate to="/users/permissions" replace />;
	}

	const seeded = role ? isSeededRole(role.name) : false;

	const handleSubmit = (data: RoleFormValues) => {
		updateRole(
			{ roleId, permissions: data.permissions },
			{
				onSuccess: () => navigate("/users/permissions")
			}
		);
	};

	const handleDelete = () => {
		deleteRole(roleId, {
			onSuccess: () => {
				setShowDeleteModal(false);
				navigate("/users/permissions");
			}
		});
	};

	return (
		<Page>
			<RoleForm
				defaultValues={defaultValues}
				availablePermissions={availablePermissions ?? []}
				onSubmit={handleSubmit}
				isSubmitting={isUpdating}
				isEdit
				isNameReadOnly
				onDelete={seeded ? undefined : () => setShowDeleteModal(true)}
				backPath="/users/permissions"
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete role?"
				description={`This will remove the "${role?.name}" role. Users currently assigned this role will lose its permissions. This action cannot be undone.`}
				onCancel={() => {
					if (!isDeleting) setShowDeleteModal(false);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditRole;
